import { Mui } from "@osu/react-ui";
import { useState } from 'react';
import { skipToken } from "@reduxjs/toolkit/query";
import {
    useBulkAdminAdmitScholarsMutation,
    useBulkAdminDenyScholarsMutation,
    useGetScholarsProgramsQuery,
} from '../../services/graphql/api';
import DialogSummaryBox from "../../Common/components/DialogSummaryBox";

// has572 — Scholars-only bulk admin recommendation from Advanced Search. This mirrors the Honors
// "Administrative Recommendation" UX: a SINGLE Actions menu item opens one modal, and the admin first
// picks the decision to post (Admit vs Deny). The modal then reveals the fields that decision needs:
//   - ADMIT: a target sub-program (capacity + admit communication resolve from it). The reason is the
//     fixed "Admin Recommendation" (stamped server-side), so there is no reason picker.
//   - DENY: one of the three fixed deny scenarios. Denied applicants are parked as a pending deny on
//     Recommendations to Approve for final approval.
// The two distinct backend mutations are preserved; this dialog just routes to the right one.
export const RECOMMENDATIONS = {
    Admit: "Admit",
    Deny: "Deny",
};

export const SCHOLARS_BULK_DENY_REASONS = [
    "Not invited to preference (too late / does not meet eligibility criteria)",
    "Invited to choose preferences but never submitted",
    "Preferenced and reviewed but not admitted to any program",
];

const DialogScholarsBulkRecommendation = ({ openDialog, setOpenDialog, searchParams, skip, totalCount }) => {
    const [showSuccess, setShowSuccess] = useState(false);
    const [recommendation, setRecommendation] = useState("");
    const [subProgramId, setSubProgramId] = useState("");
    const [selectedReason, setSelectedReason] = useState("");

    const [bulkAdminAdmitScholars, { isLoading: isAdmitting }] = useBulkAdminAdmitScholarsMutation();
    const [bulkAdminDenyScholars, { isLoading: isDenying }] = useBulkAdminDenyScholarsMutation();

    const isAdmit = recommendation === RECOMMENDATIONS.Admit;
    const isDeny = recommendation === RECOMMENDATIONS.Deny;
    const isSubmitting = isAdmitting || isDenying;

    // Only load sub-programs once the admin has chosen Admit (and the dialog is open).
    const { data: programs = [], isFetching: programsLoading } = useGetScholarsProgramsQuery(
        skip || !openDialog || !isAdmit ? skipToken : undefined
    );

    if (skip) { return null; }

    const dialogMessage = !recommendation
        ? "Choose the administrative recommendation to post for every applicant in the selected population."
        : isAdmit
            ? "This will ADMIT every applicant in the selected population into the sub-program you choose. "
              + "The recommendation reason is recorded as \"Admin Recommendation\"."
            : "This will DENY every applicant in the selected population. Choose the reason that applies. "
              + "Denied applicants are moved to Recommendations to Approve as a pending deny for final approval.";

    // Submit is only enabled once the decision-specific field is satisfied.
    const disableSubmit = isSubmitting
        || !recommendation
        || (isAdmit && (programsLoading || !subProgramId))
        || (isDeny && !selectedReason);

    async function confirmAction(e) {
        e.preventDefault();
        e.stopPropagation();
        if (disableSubmit) { return; }

        try {
            if (isAdmit) {
                const input = { criteria: searchParams, subProgramId };
                const result = await bulkAdminAdmitScholars({ input });
                if (result.data?.status === "success") {
                    setShowSuccess(true);
                } else {
                    console.error("Scholars bulk admit failed:", result);
                }
            } else if (isDeny) {
                const input = { criteria: searchParams, reason: selectedReason };
                const result = await bulkAdminDenyScholars({ input });
                if (result.data?.status === "success") {
                    setShowSuccess(true);
                } else {
                    console.error("Scholars bulk deny failed:", result);
                }
            }
        } catch (error) {
            console.error("Error submitting Scholars bulk recommendation:", error);
        }
    }

    // has572 — Deliberately do NOT clear the Advanced Search criteria or results grid on close.
    // The admin needs the search attributes and results to remain on the page after a bulk action.
    const handleClose = () => {
        setOpenDialog(false);
        setShowSuccess(false);
        setRecommendation("");
        setSubProgramId("");
        setSelectedReason("");
    };

    const handleDialogClose = (event, reason) => {
        if (showSuccess && reason === 'backdropClick') { return; }
        if (!showSuccess) { handleClose(); }
    };

    const successMessage = isAdmit
        ? "Scholars bulk admit initiated successfully!"
        : "Scholars bulk deny initiated successfully!";

    return (
        <Mui.Dialog
            open={openDialog}
            onClose={handleDialogClose}
            aria-labelledby="scholars-bulk-recommendation-dialog-title"
            aria-describedby="scholars-bulk-recommendation-dialog-description"
            fullWidth
            maxWidth="sm">
            <Mui.DialogTitle id="scholars-bulk-recommendation-dialog-title">Bulk Admin Recommendation</Mui.DialogTitle>

            {showSuccess && (
                <Mui.Alert severity="info" sx={{ margin: 2, marginBottom: 0 }}>
                    {successMessage}
                </Mui.Alert>
            )}

            <Mui.DialogContent>
                {!showSuccess && (
                    <Mui.DialogContentText id="scholars-bulk-recommendation-dialog-description" style={{ marginBottom: '1rem' }}>
                        {dialogMessage}
                    </Mui.DialogContentText>
                )}

                {!showSuccess && (
                    <Mui.Box style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Mui.FormControl fullWidth required>
                            <Mui.InputLabel id="scholars-bulk-recommendation-label">Recommendation</Mui.InputLabel>
                            <Mui.Select
                                labelId="scholars-bulk-recommendation-label"
                                id="scholars-bulk-recommendation-select"
                                value={recommendation}
                                label="Recommendation"
                                onChange={(e) => {
                                    // Switching the decision clears the decision-specific field so a stale
                                    // sub-program / reason can't be submitted against the wrong mutation.
                                    setRecommendation(e.target.value);
                                    setSubProgramId("");
                                    setSelectedReason("");
                                }}
                            >
                                <Mui.MenuItem value=""> - Choose a Recommendation - </Mui.MenuItem>
                                <Mui.MenuItem value={RECOMMENDATIONS.Admit}>{RECOMMENDATIONS.Admit}</Mui.MenuItem>
                                <Mui.MenuItem value={RECOMMENDATIONS.Deny}>{RECOMMENDATIONS.Deny}</Mui.MenuItem>
                            </Mui.Select>
                        </Mui.FormControl>

                        {isAdmit && (programsLoading ? (
                            <Mui.Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                                <Mui.CircularProgress />
                            </Mui.Box>
                        ) : (
                            <Mui.FormControl sx={{ marginTop: "1rem" }} fullWidth required>
                                <Mui.InputLabel id="scholars-bulk-admit-program-label">Sub-Program</Mui.InputLabel>
                                <Mui.Select
                                    labelId="scholars-bulk-admit-program-label"
                                    id="scholars-bulk-admit-program-select"
                                    value={subProgramId}
                                    onChange={(e) => setSubProgramId(e.target.value)}
                                    label="Sub-Program"
                                >
                                    <Mui.MenuItem value=""> - Choose a Sub-Program - </Mui.MenuItem>
                                    {(programs || []).map((program) => (
                                        <Mui.MenuItem key={program.subProgramId} value={program.subProgramId} disabled={program.isOpen === false}>
                                            {program.label || program.subProgramId}{program.isOpen === false ? " (Closed)" : ""}
                                        </Mui.MenuItem>
                                    ))}
                                </Mui.Select>
                            </Mui.FormControl>
                        ))}

                        {isDeny && (
                            <Mui.FormControl sx={{ marginTop: "1rem" }} fullWidth required>
                                <Mui.InputLabel id="scholars-bulk-deny-reason-label">Reason</Mui.InputLabel>
                                <Mui.Select
                                    labelId="scholars-bulk-deny-reason-label"
                                    id="scholars-bulk-deny-reason-select"
                                    value={selectedReason}
                                    onChange={(e) => setSelectedReason(e.target.value)}
                                    label="Reason"
                                >
                                    <Mui.MenuItem value=""> - Choose a Reason - </Mui.MenuItem>
                                    {SCHOLARS_BULK_DENY_REASONS.map((option) => (
                                        <Mui.MenuItem key={option} value={option}>{option}</Mui.MenuItem>
                                    ))}
                                </Mui.Select>
                            </Mui.FormControl>
                        )}

                        {recommendation && <DialogSummaryBox totalCount={totalCount} />}
                    </Mui.Box>
                )}
            </Mui.DialogContent>

            <Mui.DialogActions className="padding-2">
                {showSuccess ? (
                    <Mui.Button color="primary" style={{ border: "none" }} variant="contained" onClick={handleClose}>
                        Close
                    </Mui.Button>
                ) : (
                    <>
                        <Mui.Button
                            color="secondary"
                            style={{ border: "none" }}
                            variant="outlined"
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Mui.Button>
                        <Mui.Button
                            disabled={disableSubmit}
                            color="primary"
                            style={{ border: "none" }}
                            variant="contained"
                            onClick={confirmAction}
                        >
                            {isSubmitting && <Mui.CircularProgress size={20} style={{ marginRight: '.5rem' }} />}
                            Submit
                        </Mui.Button>
                    </>
                )}
            </Mui.DialogActions>
        </Mui.Dialog>
    );
};

export default DialogScholarsBulkRecommendation;
